import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import React, { useEffect, useState } from 'react';

import { IconPaginationNext, IconPaginationPrev } from '@/assets';
import useCreateQueryString from '@/hooks/useCreateQueryString';
import { getNumberOfPage } from '@/utils';

import { Buttons } from '../button';

const Pagination: React.FC<{
  startPage: number;
  total: number;
  pageSize: number;
  onChange?: (e: number | string) => void;
  className?: string;
  queryPrams?: { key: string; value: any }[];
  queryOptions?: NavigateOptions;
}> = ({
  startPage,
  onChange,
  total,
  pageSize,
  className = '',
  queryPrams = [],
  queryOptions = {},
}) => {
  const [currentPage, setCurrentPage] = useState<number>(startPage);
  const [pageInput, setPageInput] = useState<string>(startPage.toString());
  const numOfPage = getNumberOfPage(total, pageSize);
  const createQueryString = useCreateQueryString();

  useEffect(() => {
    if (onChange) {
      onChange(currentPage);
    }
    createQueryString(
      ['page'].concat(queryPrams.map((item) => item.key)),
      [currentPage].concat(queryPrams.map((item) => item.value)),
      queryOptions,
    );
  }, [currentPage]);

  useEffect(() => {
    if (startPage > numOfPage || startPage < 1) return;
    setCurrentPage(startPage);
    setPageInput(startPage.toString());
  }, [startPage]);

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    if (val === '') setPageInput('');
    if (!val.match(/^[1-9]\d*$/)) return;
    setPageInput(val);
  };

  const handleChangePage = (type: 'next' | 'prev') => {
    if (type === 'prev') {
      const val = currentPage > 1 ? currentPage - 1 : currentPage;
      setPageInput(val.toString());
      setCurrentPage(val);
    } else {
      const val = currentPage < numOfPage ? currentPage + 1 : currentPage;
      setPageInput(val.toString());
      setCurrentPage(val);
    }
  };

  const onKeyDownInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13) {
      const page = Number(pageInput);
      if (!page || page < 1) return;
      setCurrentPage(page);
    }
  };

  return (
    <div className={`flex w-full items-center justify-between ${className}`}>
      <div className="w-[calc(100%/3)] sm:hidden" />
      <div className="flex w-[calc(100%/3)] items-center justify-center sm:w-[calc(50%)] sm:justify-start">
        {currentPage > 1 && (
          <Buttons
            className="mr-4 h-[44px] w-[44px] min-w-0 rounded-[50%] p-0 sm:mr-2 sm:h-[38px] sm:w-[38px]"
            typeBtn="secondary"
            onClick={() => handleChangePage('prev')}
          >
            <IconPaginationPrev />
          </Buttons>
        )}
        <Buttons
          className="h-[44px] px-[16px] text-[15px] sm:h-[38px] sm:min-w-0 sm:!text-[12px]"
          disabled={currentPage >= numOfPage}
          typeBtn={currentPage >= numOfPage ? 'disabled' : 'primary'}
          onClick={() => handleChangePage('next')}
        >
          Next page <IconPaginationNext className="sm:hidden" />
        </Buttons>
      </div>
      <div className="flex w-[calc(100%/3)] items-center justify-end text-[14px] text-black sm:w-[calc(50%)]">
        <span>Page</span>
        <input
          value={pageInput}
          onChange={onChangeInput}
          onKeyDown={onKeyDownInput}
          type="text"
          className="mx-2 h-[32px] w-[50px] rounded-[6px] border-[1px] border-gray-200 p-3 text-center outline-none"
        />
        <span>of {numOfPage}</span>
      </div>
    </div>
  );
};

export default Pagination;
